import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, query, where } from "firebase/firestore";
import app from "../../firebase";

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalStudents: 0,
    byStatus: {},
    byProgram: {},
    byYearLevel: {},
    paymentStats: {
      totalRevenue: 0,
      fullyPaid: 0,
      partiallyPaid: 0,
      unpaid: 0,
      byCashWithDiscount: 0,
      byCashWithoutDiscount: 0,
      byInstallment: 0
    },
    requirementsStats: {
      cleared: 0,
      pending: 0,
      deficient: 0
    }
  });

  useEffect(() => {
    fetchStudentsAndAnalyze();
  }, []);

  const fetchStudentsAndAnalyze = async () => {
    setLoading(true);
    try {
      const db = getFirestore(app);
      const studentsRef = collection(db, "students");
      const snap = await getDocs(studentsRef);
      
      const studentsData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudents(studentsData);
      
      // Calculate analytics
      const stats = {
        totalStudents: studentsData.length,
        byStatus: {},
        byProgram: {},
        byYearLevel: {},
        paymentStats: {
          totalRevenue: 0,
          fullyPaid: 0,
          partiallyPaid: 0,
          unpaid: 0,
          byCashWithDiscount: 0,
          byCashWithoutDiscount: 0,
          byInstallment: 0
        },
        requirementsStats: {
          cleared: 0,
          pending: 0,
          deficient: 0
        }
      };

      studentsData.forEach(student => {
        // Status breakdown
        const status = student.registrationStatus || "unknown";
        stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;

        // Program breakdown
        const program = student.program || "Unknown";
        stats.byProgram[program] = (stats.byProgram[program] || 0) + 1;

        // Year Level breakdown
        const yearLevel = student.yearLevel || student.preRegistration?.entryLevel || "Unknown";
        stats.byYearLevel[yearLevel] = (stats.byYearLevel[yearLevel] || 0) + 1;

        // Payment statistics
        const payments = student.payments || {};
        const installmentPayments = student.installmentPayments || {};
        const miscHistory = student.miscellaneousFeesHistory || {};

        Object.keys(miscHistory).forEach(termKey => {
          const miscEntries = miscHistory[termKey] || [];
          const latestMisc = Array.isArray(miscEntries) && miscEntries.length > 0 
            ? miscEntries[miscEntries.length - 1] 
            : null;

          if (latestMisc) {
            const totalAmount = latestMisc.totalAmount || 0;
            const termPayments = payments[termKey] || [];
            const termInstallments = installmentPayments[termKey] || [];

            let totalPaid = 0;
            let paymentMethod = null;

            // Calculate total paid from cash payments
            termPayments.forEach(payment => {
              totalPaid += payment.amount || 0;
              if (payment.paymentOption === "CASH PAYMENT WITH DISCOUNT") {
                stats.paymentStats.byCashWithDiscount += payment.amount || 0;
                paymentMethod = "cashWithDiscount";
              } else if (payment.paymentOption === "CASH PAYMENT WITHOUT DISCOUNT") {
                stats.paymentStats.byCashWithoutDiscount += payment.amount || 0;
                paymentMethod = "cashWithoutDiscount";
              }
            });

            // Calculate total paid from installments
            if (termInstallments.length > 0) {
              const installmentData = termInstallments[0];
              ['Downpayment', '1st Installment', '2nd Installment', '3rd Installment', '4th Installment'].forEach(key => {
                if (installmentData[key] && installmentData[key].date) {
                  totalPaid += installmentData[key].amount || 0;
                  stats.paymentStats.byInstallment += installmentData[key].amount || 0;
                  paymentMethod = "installment";
                }
              });
            }

            stats.paymentStats.totalRevenue += totalPaid;

            const balance = totalAmount - totalPaid;
            if (balance === 0) {
              stats.paymentStats.fullyPaid++;
            } else if (totalPaid > 0) {
              stats.paymentStats.partiallyPaid++;
            } else {
              stats.paymentStats.unpaid++;
            }
          }
        });

        // Requirements clearance
        const allCleared = student.allRequirementsCleared === true;
        const hasDeficiency = student.hasRequirementDeficiency === true;
        
        if (allCleared) {
          stats.requirementsStats.cleared++;
        } else if (hasDeficiency) {
          stats.requirementsStats.deficient++;
        } else {
          stats.requirementsStats.pending++;
        }
      });

      setAnalytics(stats);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, color = "#0f5f3b", icon }) => (
    <div style={{
      background: "#fff",
      borderRadius: 12,
      padding: "24px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      border: `3px solid ${color}`,
      position: "relative",
      overflow: "hidden"
    }}>
      <div style={{
        position: "absolute",
        top: -20,
        right: -20,
        fontSize: 80,
        opacity: 0.1,
        color: color
      }}>
        {icon}
      </div>
      <div style={{ fontSize: 14, color: "#666", marginBottom: 8, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 36, fontWeight: 700, color: color, marginBottom: 4 }}>{value}</div>
      {subtitle && <div style={{ fontSize: 12, color: "#888" }}>{subtitle}</div>}
    </div>
  );

  const BreakdownCard = ({ title, data, color = "#0f5f3b" }) => (
    <div style={{
      background: "#fff",
      borderRadius: 12,
      padding: "24px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
    }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: "#333", marginBottom: 16 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {Object.entries(data).map(([key, value]) => {
          const total = Object.values(data).reduce((sum, v) => sum + v, 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          
          return (
            <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 14, color: "#555", fontWeight: 600 }}>{key}</span>
                <span style={{ fontSize: 14, color: "#333", fontWeight: 700 }}>{value}</span>
              </div>
              <div style={{ 
                width: "100%", 
                height: 8, 
                background: "#e0e0e0", 
                borderRadius: 4,
                overflow: "hidden"
              }}>
                <div style={{ 
                  width: `${percentage}%`, 
                  height: "100%", 
                  background: color,
                  transition: "width 0.3s ease"
                }} />
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>{percentage}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: 40 }}>
        <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 24, color: "#0f5f3b" }}>Analytics Dashboard</div>
        <div style={{ textAlign: "center", padding: 48, color: "#666" }}>Loading analytics data...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 40 }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: "#0f5f3b", marginBottom: 8 }}>Analytics Dashboard</div>
        <div style={{ fontSize: 14, color: "#666" }}>Comprehensive overview of enrollment and payment statistics</div>
      </div>

      {/* Phase 1 Metrics */}
      
      {/* Top Stats Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20, marginBottom: 32 }}>
        <StatCard 
          title="Total Students" 
          value={analytics.totalStudents}
          subtitle="All registered students"
          color="#0f5f3b"
          icon="👥"
        />
        <StatCard 
          title="Enrolled" 
          value={analytics.byStatus.enrolled || 0}
          subtitle="Officially enrolled students"
          color="#2e7d32"
          icon="✓"
        />
        <StatCard 
          title="Submitted" 
          value={analytics.byStatus.submitted || 0}
          subtitle="Pending enrollment approval"
          color="#ff6f2e"
          icon="📝"
        />
        <StatCard 
          title="Total Revenue" 
          value={`₱${analytics.paymentStats.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          subtitle="All payments collected"
          color="#1976d2"
          icon="💰"
        />
      </div>

      {/* Enrollment Statistics */}
      <div style={{ 
        background: "#fff", 
        borderRadius: 12, 
        padding: "32px", 
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        marginBottom: 32
      }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#0f5f3b", marginBottom: 24 }}>Enrollment Statistics</div>
        
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
          <BreakdownCard 
            title="By Registration Status" 
            data={analytics.byStatus}
            color="#0f5f3b"
          />
          <BreakdownCard 
            title="By Program/Department" 
            data={analytics.byProgram}
            color="#1976d2"
          />
          <BreakdownCard 
            title="By Year Level" 
            data={analytics.byYearLevel}
            color="#e67e22"
          />
        </div>
      </div>

      {/* Payment Collection Summary */}
      <div style={{ 
        background: "#fff", 
        borderRadius: 12, 
        padding: "32px", 
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        marginBottom: 32
      }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#0f5f3b", marginBottom: 24 }}>Payment Collection Summary</div>
        
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20, marginBottom: 24 }}>
          <div style={{ textAlign: "center", padding: 20, background: "#e8f5e9", borderRadius: 8 }}>
            <div style={{ fontSize: 14, color: "#2e7d32", marginBottom: 8 }}>Fully Paid</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: "#2e7d32" }}>{analytics.paymentStats.fullyPaid}</div>
          </div>
          <div style={{ textAlign: "center", padding: 20, background: "#fff3e0", borderRadius: 8 }}>
            <div style={{ fontSize: 14, color: "#e65100", marginBottom: 8 }}>Partially Paid</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: "#e65100" }}>{analytics.paymentStats.partiallyPaid}</div>
          </div>
          <div style={{ textAlign: "center", padding: 20, background: "#ffebee", borderRadius: 8 }}>
            <div style={{ fontSize: 14, color: "#c62828", marginBottom: 8 }}>Unpaid</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: "#c62828" }}>{analytics.paymentStats.unpaid}</div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #e0e0e0", paddingTop: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#333", marginBottom: 16 }}>Revenue by Payment Method</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: "#f5f5f5", borderRadius: 6 }}>
              <span style={{ color: "#555" }}>Cash with Discount</span>
              <span style={{ fontWeight: 700, color: "#2e7d32" }}>
                ₱{analytics.paymentStats.byCashWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: "#f5f5f5", borderRadius: 6 }}>
              <span style={{ color: "#555" }}>Cash without Discount</span>
              <span style={{ fontWeight: 700, color: "#1976d2" }}>
                ₱{analytics.paymentStats.byCashWithoutDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", background: "#f5f5f5", borderRadius: 6 }}>
              <span style={{ color: "#555" }}>Installment</span>
              <span style={{ fontWeight: 700, color: "#e67e22" }}>
                ₱{analytics.paymentStats.byInstallment.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Requirements & Clearance */}
      <div style={{ 
        background: "#fff", 
        borderRadius: 12, 
        padding: "32px", 
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
      }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#0f5f3b", marginBottom: 24 }}>Requirements & Clearance Status</div>
        
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>
          <div style={{ 
            padding: 24, 
            background: "linear-gradient(135deg, #2e7d32 0%, #43a047 100%)",
            borderRadius: 12,
            color: "#fff",
            boxShadow: "0 4px 12px rgba(46,125,50,0.3)"
          }}>
            <div style={{ fontSize: 14, marginBottom: 8, opacity: 0.9 }}>Cleared Students</div>
            <div style={{ fontSize: 36, fontWeight: 700 }}>{analytics.requirementsStats.cleared}</div>
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
              {((analytics.requirementsStats.cleared / analytics.totalStudents) * 100).toFixed(1)}% of total
            </div>
          </div>
          
          <div style={{ 
            padding: 24, 
            background: "linear-gradient(135deg, #f57c00 0%, #fb8c00 100%)",
            borderRadius: 12,
            color: "#fff",
            boxShadow: "0 4px 12px rgba(245,124,0,0.3)"
          }}>
            <div style={{ fontSize: 14, marginBottom: 8, opacity: 0.9 }}>Pending Requirements</div>
            <div style={{ fontSize: 36, fontWeight: 700 }}>{analytics.requirementsStats.pending}</div>
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
              {((analytics.requirementsStats.pending / analytics.totalStudents) * 100).toFixed(1)}% of total
            </div>
          </div>
          
          <div style={{ 
            padding: 24, 
            background: "linear-gradient(135deg, #c62828 0%, #d32f2f 100%)",
            borderRadius: 12,
            color: "#fff",
            boxShadow: "0 4px 12px rgba(198,40,40,0.3)"
          }}>
            <div style={{ fontSize: 14, marginBottom: 8, opacity: 0.9 }}>Deficient Students</div>
            <div style={{ fontSize: 36, fontWeight: 700 }}>{analytics.requirementsStats.deficient}</div>
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
              {((analytics.requirementsStats.deficient / analytics.totalStudents) * 100).toFixed(1)}% of total
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
